import styles from './SignedInHeaderSection.module.css';
import { useNavigate } from 'react-router-dom';
import { useDescope, useSession } from '@descope/react-sdk';
import { useSessionClaims } from '@percihealth/react-auth';
import { useFirebase } from '../../../context';
import DropdownIcon from '../../icons/DropdownIcon';
import { useState } from 'react';

export default function SignedInHeaderSection({
  // the referral apps have no profile route (and their partner users cannot
  // call the staff profile API), so they render the menu without the entry
  showProfile = true,
}: {
  showProfile?: boolean;
}) {
  // Identity comes from the Descope session, not the Firebase user: on a reload
  // Firebase can briefly restore a previous account while Descope loads the
  // current one, and rendering the Firebase user would show the wrong name and
  // expose its menu. Render only for an authenticated Descope session.
  const { isAuthenticated } = useSession();
  // The session TOKEN's claims: `useSession().claims` carries no `name`, so
  // the menu showed every user their email instead of their name.
  const sessionClaims = useSessionClaims();
  const { auth } = useFirebase();
  const descope = useDescope();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);

  if (!isAuthenticated) return <></>;

  // Treat an empty string as absent (as the backend does for the display
  // name), so an empty name claim falls back to the email, not a blank label.
  const name =
    typeof sessionClaims?.name === 'string' && sessionClaims.name
      ? sessionClaims.name
      : undefined;
  const email =
    typeof sessionClaims?.email === 'string' && sessionClaims.email
      ? sessionClaims.email
      : undefined;

  return (
    <div
      className={styles.container}
      onBlur={(e) => {
        // Close when focus leaves the menu as a whole, not when it moves to
        // one of its buttons: a button takes focus on mousedown (Chrome,
        // Firefox), which blurs this container before the mouseup - closing
        // unconditionally unmounted the button mid-click, so Profile and
        // Sign-out never received the click.
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setExpanded(false);
        }
      }}
      tabIndex={0}
    >
      <div className={styles['toggle']} onClick={() => setExpanded(!expanded)}>
        {name ?? email ?? 'Account'} <DropdownIcon className={styles.icon} />
      </div>

      {expanded && (
        <div className={styles.menus}>
          {showProfile && (
            <Menu>
              <button
                type="button"
                onClick={() => {
                  setExpanded(false);
                  navigate('/Profile');
                }}
              >
                Profile
              </button>
            </Menu>
          )}
          <Menu>
            <button
              type="button"
              onClick={async () => {
                // Clear the Descope session first: it is the credential, and
                // once it is gone FirebaseSessionSync tears the Firebase
                // session down on its own. Sign Firebase out here too for
                // immediacy. Navigate ONLY if the Descope logout succeeded -
                // otherwise the session is still live, so stay put rather than
                // land on /login still signed in (which would bounce back in).
                try {
                  await descope.logout();
                } catch (err) {
                  console.error(err);
                  return;
                }
                // Descope (the credential) is cleared; await Firebase too so
                // the subscription session is not still active on the next
                // route. It is best-effort - the sync also tears it down once
                // Descope is gone - so a failure here does not block leaving.
                try {
                  await auth.signOut();
                } catch (err) {
                  console.error(err);
                }
                setExpanded(false);
                navigate('/login');
              }}
            >
              Sign-out
            </button>
          </Menu>
        </div>
      )}
    </div>
  );
}

const Menu = ({ children }: { children: React.ReactNode }) => {
  return <div className={styles.menu}>{children}</div>;
};
